import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { getRequest, tabulatorGet } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import {
  PrintIcon,
  SearchIcon,
  ExportIcon,
  RefreshIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  CollapseTreeIcon,
  ExpandTreeIcon,
  BranchTreeIcon,
  PaperClipIcon,
} from '../../svg/svgIcons';
import UtilContext from '../../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../../components/maintenance/MaintenanceMaskDialogCheckbox';
import AuthContext from '../../../components/AuthContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { MdSearch } from 'react-icons/md';
import { MdSearchOff } from 'react-icons/md';
import ReactDOMServer from 'react-dom/server';
import { DateTime } from 'luxon';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createSaveUserMaskColumns,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../../utils/maintenanceTabulatorMaskActions';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  safeParse,
  mergePagedStandardListFiltersAndSorters,
  appendMaintenanceCompanyCertStateListPagedFilter,
  applyMaintenanceCompanyCertStateListColumnLayout,
  restoreMaintenanceCompanyCertStateListUserMaskToTable,
  MAINTENANCE_COMPANY_CERT_STATE_LIST_FIELD_ORDER,
} from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, TABULATOR_LIST_ID_MIN_WIDTH } from '../../../utils/tabulatorColumnMinWidth';
import {
  buildAuditTabulatorColumns,
  mergeColumnVisibilityWithAuditDefaults,
  TABULATOR_AUDIT_PROFILES,
} from '../../../utils/tabulatorAuditColumns';
import { getPusherChannel } from '../../../utils/pusherChannel';
import { primeMultiSelectHeaderFilter } from '../../../utils/primeMultiSelectHeaderFilter';
import {
  getMntnSemaforColor,
  getSemaforColorClass,
  showDeactivatedInLists,
  showExcludedServiceCompaniesInLists,
} from '../../../utils/moduleSettings';
import { LocaleContext } from '../../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../../constants/brandingDisplay';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../../utils/printChrome';
const MAINTENANCE_COMPANY_CERT_STATE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  'company.name': 'MaintenanceCompanyCertStateList.Text.Nazov_serv_firmy',
  'description': 'Common.Description',
  'name': 'Common.CertificateName',
  'custom_start_of_validity': 'Common.ValidFrom',
  'custom_end_of_validity': 'Common.ValidTo',
  'custom_expiration': 'Common.Expiration',
  'record_description': 'Common.CertificateDescription',
  'responsible.name': 'Common.Column.Responsible',
  'certificate_status': 'Common.CertificateStatus',
  'state': 'Common.Status',
  'id': 'MachineTypeList.Column.Id',
  'protocol': 'Common.Protocol',
});



const MASK_PAGE = 'MaintenanceCompanyCertStateList';

const DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  'company.name': true,
  description: true,
  name: true,
  custom_start_of_validity: true,
  custom_end_of_validity: true,
  custom_expiration: true,
  record_description: true,
  'responsible.name': true,
  certificate_status: true,
  state: true,
  protocol: true,
  created_at: true,
  updated_at: true,
});

function mergeCertStateListColumnVisibilityFromSaved(saved) {
  const merged = mergeColumnVisibilityWithAuditDefaults(DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY, saved);
  if (saved && typeof saved === 'object' && saved.description !== undefined && saved.record_description === undefined) {
    merged.record_description = saved.description !== false;
  }
  return merged;
}

function applyCertStateListDataColumnVisibility(table, visibility) {
  if (!table) return;
  for (const field of Object.keys(DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY)) {
    if (visibility[field] === false) table.hideColumn?.(field);
    else table.showColumn?.(field);
  }
}

function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function MaintenanceCompanyCertStateList({ preview = null, preview_period = null, preview_segment = null }) {
  const [state] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const module_settings = state.module_settings || [];
  const {
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openFilePreview,
    getDateFormat,
    getUserColor,
    getUserInitials,
    openChosenNotificationPreferences,
  } = useContext(UtilContext);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  /** Obdobie z dashboardu — bez Redux masiek (pôvodné správanie maintenance). */
  const isPreviewPeriodMode = preview_period != null;
  /** Semafor preview (after/15/30/others) — skryť Akcie filter, maska z Redux ostáva. */
  const hideRemoteFilterAkcie = preview != null || preview_period != null;

  const [favoriteIcon] = useState(false);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY }));
  const [maskVisible, setMaskVisible] = useState(false);

  const ref = useRef(null);
  const elementRef = useRef(null);
  const overlayRef = useRef(null);
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 50;
  }, [state.module_settings]);
  const showExcludedCompanies = showExcludedServiceCompaniesInLists(module_settings);
  const showDeactivatedCerts = showDeactivatedInLists(module_settings, 'maintenance');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const refreshFromStart = useCallback(
    () => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingData')),
    [getTable]
  );

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  const remoteHeaderFilter = useCallback(() => true, []);

  function matchAny(data, filterParams) {
    const regex = RegExp(filterParams.value, 'i');
    for (const key in data) {
      if (regex.test(data[key]) === true) return true;
    }
    return false;
  }

  function searchTable() {
    const table = getTable();
    if (!table) return;
    if (searchValue && String(searchValue).trim() !== '' && searchValue !== ' ') {
      table.setFilter(matchAny, { value: searchValue });
      setSearchIconColor('active');
    } else {
      table.clearFilter?.();
      setSearchIconColor('');
      setSearchValue('');
      refreshFromStart();
    }
    setVisibleSearch(false);
  }

  const onSelectRow = () => {};

  function MaintenanceCompanyNameFormatter(props) {
    const rowData = props.cell.getRow().getData();
    if (rowData?._children) return <div className="training_name">{rowData.company?.name}</div>;
    return '';
  }

  function NameFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const parent = props.cell.getRow()?.getTreeParent();
    if (rowData?._children) return <div className="training_name">{rowData.name}</div>;
    if (parent) {
      const parentArray = parent._row.data._children;
      for (let i = 0; i < parentArray.length; i++) {
        if (parentArray[i].id === rowData.id) {
          if (i === parentArray.length - 1) return <div className="training_name">Prvotný certifikát</div>;
          return <div className="training_name">{parentArray.length - 1 - i}. Opakovaný certifikát</div>;
        }
      }
      return <div className="training_name">{parent._row.data.name}</div>;
    }
    return '';
  }

  function viewFile(file_id) {
    getRequest(`/api/company_cert_records/protocol/${file_id}`).then((response) => {
      if (response?.data?.url) openFilePreview({ url: response.data.url, filename: response.data.filename });
    });
  }

  function Protocol(props) {
    const rowData = props.cell.getRow().getData();
    const isParentRow = rowData?._children !== undefined && Array.isArray(rowData._children) && rowData._children.length > 0;
    const recordFiles = isParentRow ? rowData._children[0]?.files || [] : rowData.files || [];
    const hasFiles = Array.isArray(recordFiles) && recordFiles.length > 0;
    if (!hasFiles) return null;
    return (
      <div className="flex flex-row">
        {recordFiles.map((img) => (
          <a
            key={img.id}
            style={{ color: 'blue', cursor: 'pointer', marginBottom: '2px', width: '24px', display: 'block' }}
            onClick={() => viewFile(img.id)}
            title={img.filename}
          >
            <PaperClipIcon />
          </a>
        ))}
      </div>
    );
  }

  function EndOfValidity(props) {
    const rowData = props.cell.getRow().getData();
    if (rowData?.end_of_validity) return DateTime.fromISO(rowData.end_of_validity).toFormat(dateFmt);
    if (rowData?._children?.length > 0) return DateTime.fromISO(rowData._children[0].end_of_validity).toFormat(dateFmt);
    return <span className="text-red-500">{t('Ui.R67.Necertifikovane')}</span>;
  }

  function StartOfValidity(props) {
    const rowData = props.cell.getRow().getData();
    if (rowData?.start_of_validity) return DateTime.fromISO(rowData.start_of_validity).toFormat(dateFmt);
    if (rowData?._children?.length > 0) return DateTime.fromISO(rowData._children[0].start_of_validity).toFormat(dateFmt);
    return <span className="text-red-500">{t('Ui.R67.Necertifikovane')}</span>;
  }

  function StateFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const parent = props.cell.getRow()?.getTreeParent();
    if (parent) {
      const parentArray = parent._row.data._children;
      for (let i = 0; i < parentArray.length; i++) {
        if (parentArray[i].id === rowData.id) {
          if (i === parentArray.length - 1) {
            if (new Date(parentArray[i]?.end_of_validity) < new Date()) return 'NOK';
            return 'OK';
          }
          return 'OK';
        }
      }
    }
    if (rowData?._children?.length > 0) {
      if (new Date(rowData?._children[0]?.end_of_validity) < new Date()) return 'NOK';
      return 'OK';
    }
    return 'NOK';
  }

  function CertificateStatusFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const v = rowData?._children?.length > 0 ? rowData._children[0]?.deactivation_date : rowData?.deactivation_date;
    return <span>{v ? t('Status.Deactivated') : 'Aktívny'}</span>;
  }

  function getStateLabel(rowData) {
    if (rowData?._children?.length > 0) {
      if (new Date(rowData._children[0]?.end_of_validity) < new Date()) return 'NOK';
      return 'OK';
    }
    if (rowData?.end_of_validity && new Date(rowData.end_of_validity) < new Date()) return 'NOK';
    if (rowData?.start_of_validity || rowData?.end_of_validity) return 'OK';
    return 'NOK';
  }

  function ExpirationFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const hasDeactivation = rowData?.deactivation_date ?? rowData?._children?.[0]?.deactivation_date;
    if (hasDeactivation) {
      const color = getMntnSemaforColor(module_settings, 'DEA');
      const colorClass = getSemaforColorClass(color);
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${colorClass}`} title="DEA" />
          <span>DEA</span>
        </span>
      );
    }
    let endDate = null;
    if (rowData?.end_of_validity) endDate = new Date(rowData.end_of_validity);
    else if (rowData?._children?.length > 0) endDate = new Date(rowData._children[0].end_of_validity);

    let bucket = '0-';
    if (endDate) {
      const now = new Date();
      if (endDate < now) bucket = '0-';
      else if (endDate <= new Date(new Date().setDate(now.getDate() + 15))) bucket = '15-';
      else if (endDate <= new Date(new Date().setDate(now.getDate() + 30))) bucket = '30-';
      else if (endDate <= new Date(new Date().setDate(now.getDate() + 45))) bucket = '45-';
      else if (endDate <= new Date(new Date().setDate(now.getDate() + 60))) bucket = '60-';
      else bucket = '61+';
    }
    const color = getMntnSemaforColor(module_settings, bucket);
    const colorClass = getSemaforColorClass(color);
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${colorClass}`} title={bucket} />
        <span>{bucket}</span>
      </span>
    );
  }

  const auditTailColumns = useMemo(
    () =>
      buildAuditTabulatorColumns({
        profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
        includeId: false,
        dateFormat: dateFmt,
        dateTimeFormat: dateTimeFmt,
        getUserColor,
        getUserInitials,
        textHeaderFilter: remoteHeaderFilter,
        remotePassthrough: true,
      }),
    [dateFmt, dateTimeFmt, getUserColor, getUserInitials, remoteHeaderFilter]
  );

  const columns = useMemo(
    () => [
      {
        title: t('MachineTypeList.Column.Id'),
        field: 'id',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: TABULATOR_LIST_ID_MIN_WIDTH,
        widthGrow: 1,
        sorter: 'number',
        hozAlign: 'left',
      },
      {
        title: t('MaintenanceCompanyCertStateList.Text.Nazov_serv_firmy'),
        field: 'company.name',
        formatter: reactFormatter(<MaintenanceCompanyNameFormatter />),
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MaintenanceCompanyCertStateList.Text.Nazov_serv_firmy')),
        widthGrow: 1,
      },
      {
        title: t('Common.Description'),
        field: 'description',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Description')),
        widthGrow: 1,
      },
      {
        title: t('Common.CertificateName'),
        field: 'name',
        formatter: reactFormatter(<NameFormatter />),
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.CertificateName')),
        widthGrow: 1,
      },
      {
        title: t('Common.ValidFrom'),
        field: 'custom_start_of_validity',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.ValidFrom')),
        widthGrow: 1,
        formatter: reactFormatter(<StartOfValidity />),
      },
      {
        title: t('Common.ValidTo'),
        field: 'custom_end_of_validity',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.ValidTo')),
        widthGrow: 1,
        formatter: reactFormatter(<EndOfValidity />),
      },
      {
        title: t('Common.Expiration'),
        field: 'custom_expiration',
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('Common.Expiration'),
          options: [
            { label: '61+', value: '61+' },
            { label: '60-', value: '60-' },
            { label: '45-', value: '45-' },
            { label: '30-', value: '30-' },
            { label: '15-', value: '15-' },
            { label: '0-', value: '0-' },
            { label: 'DEA', value: 'DEA' },
          ],
        },
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Expiration'), 'multiselect'),
        widthGrow: 1,
        formatter: reactFormatter(<ExpirationFormatter />),
      },
      {
        title: t('Common.CertificateDescription'),
        field: 'record_description',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.CertificateDescription')),
        widthGrow: 1,
      },
      {
        title: t('Common.Column.Responsible'),
        field: 'responsible.name',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Column.Responsible')),
        widthGrow: 1,
      },
      {
        title: t('Common.CertificateStatus'),
        field: 'certificate_status',
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          options: [
            { label: 'Aktívny', value: 'Aktívny' },
            { label: t('Status.Deactivated'), value: "Deaktivovaný" },
          ],
          placeholder: t('Common.CertificateStatus'),
        },
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.CertificateStatus'), 'multiselect'),
        formatter: reactFormatter(<CertificateStatusFormatter />),
      },
      {
        title: t('Common.Status'),
        field: 'state',
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          options: [
            { label: 'OK', value: 'OK' },
            { label: 'NOK', value: 'NOK' },
          ],
          placeholder: t('Common.Status'),
        },
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Status'), 'multiselect'),
        widthGrow: 1,
        formatter: reactFormatter(<StateFormatter />),
      },
      {
        title: t('Common.Protocol'),
        field: 'protocol',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Protokol'),
        widthGrow: 1,
        formatter: reactFormatter(<Protocol />),
      },
      ...auditTailColumns,
    ],
    [auditTailColumns, remoteHeaderFilter]
  );

  const listType = 'maintenance';
  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const DEDUPE_MS = 800;
    const lastHandled = { key: '', at: 0 };
    const shouldSkip = (eventType, id) => {
      if (id == null) return true;
      const key = `${eventType}-${id}`;
      const now = Date.now();
      if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
      lastHandled.key = key;
      lastHandled.at = now;
      return false;
    };
    const isMyType = (p) => p?.type === listType;
    const hasActiveRemoteHeaderFilters = () => {
      const hf = getTable()?.getHeaderFilters?.() ?? [];
      return hf.some((f) => f?.value != null && String(f.value).trim() !== '');
    };
    const onCreated = (payload) => {
      if (!isMyType(payload)) return;
      const id = payload?.id;
      if (id == null || shouldSkip('company_certificate.created', id)) return;
      if (hasActiveRemoteHeaderFilters() || isPreviewPeriodMode) {
        refreshFromStart();
        return;
      }
      getRequest(`/api/company_certificates/${id}`)
        .then((res) => {
          const row = res?.data?.data ?? res?.data ?? res;
          if (!row?.id) return;
          const data = { ...row, _children: row._children || [] };
          const table = getTable();
          const rc = table?.getRow?.(row.id);
          if (rc) rc.update(data).catch(() => refreshFromStart());
          else table?.addData?.([data]);
        })
        .catch(() => refreshFromStart());
    };
    const onUpdated = (payload) => {
      if (!isMyType(payload)) return;
      const id = payload?.id;
      if (id == null || shouldSkip('company_certificate.updated', id)) return;
      if (hasActiveRemoteHeaderFilters() || isPreviewPeriodMode) {
        refreshFromStart();
        return;
      }
      getRequest(`/api/company_certificates/${id}`)
        .then((res) => {
          const row = res?.data?.data ?? res?.data ?? res;
          if (!row?.id) return;
          const data = { ...row, _children: row._children || [] };
          const table = getTable();
          const rc = table?.getRow?.(row.id) ?? table?.getRows?.().find((r) => r.getData?.()?.id === id);
          if (rc) rc.update(data).catch(() => refreshFromStart());
          else refreshFromStart();
        })
        .catch(() => refreshFromStart());
    };
    const onDeleted = (payload) => {
      if (!isMyType(payload)) return;
      const id = payload?.id;
      if (id == null || shouldSkip('company_certificate.deleted', id)) return;
      const table = getTable();
      const row = table?.getRows?.().find((r) => r.getData?.()?.id === id);
      if (row) row.delete();
      else refreshFromStart();
    };
    const refetch = () => refreshFromStart();
    channel.bind('company_certificate.created', onCreated);
    channel.bind('company_certificate.updated', onUpdated);
    channel.bind('company_certificate.deleted', onDeleted);
    channel.bind('company_certificate.activated', refetch);
    channel.bind('company_certificate.deactivated', refetch);
    return () => {
      channel.unbind('company_certificate.created', onCreated);
      channel.unbind('company_certificate.updated', onUpdated);
      channel.unbind('company_certificate.deleted', onDeleted);
      channel.unbind('company_certificate.activated', refetch);
      channel.unbind('company_certificate.deactivated', refetch);
    };
  }, [getTable, listType, refreshFromStart, isPreviewPeriodMode]);
  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: MAINTENANCE_COMPANY_CERT_STATE_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const buildRequestParams = useCallback(
    (paramsFromTabulator = {}) => {
      const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
      if (extra.size == null) extra.size = pageSizePc;
      extra.type = 'maintenance';
      extra.show_excluded_companies = showExcludedCompanies ? '1' : '0';
      extra.show_deactivated_certs = showDeactivatedCerts ? '1' : '0';
      if (preview_period?.dateFrom != null) {
        const dFrom = preview_period.dateFrom instanceof Date ? preview_period.dateFrom : new Date(preview_period.dateFrom);
        extra.date_from = toLocalDateString(dFrom);
      }
      if (preview_period?.dateTo != null) {
        const dTo = preview_period.dateTo instanceof Date ? preview_period.dateTo : new Date(preview_period.dateTo);
        extra.date_to = toLocalDateString(dTo);
      }
      if (preview_segment != null && preview_segment !== '') extra.preview_segment = preview_segment;
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: isPreviewPeriodMode ? null : state.user_mask_filters,
        maskPage: MASK_PAGE,
        appendFilter: appendMaintenanceCompanyCertStateListPagedFilter,
      });
      if (searchValue && String(searchValue).trim() !== '') extra.search = String(searchValue).trim();
      return extra;
    },
    [pageSizePc, preview_period, preview_segment, isPreviewPeriodMode, getTable, searchValue, state.user_mask_filters, showExcludedCompanies, showDeactivatedCerts]
  );

  const fetchCompanyCertPaged = useCallback((url, merged) => {
    return tabulatorGet(url, merged).then((r) => {
      const raw = r?.data ?? [];
      const data = raw.map((c) => ({
        ...c,
        _children: c._children ?? [],
      }));
      return { data, last_page: r?.last_page ?? 1, total: r?.total ?? 0 };
    });
  }, []);

  const ajaxRequestFunc = useMemo(
    () => createDedupedAjaxRequest(buildRequestParams, fetchCompanyCertPaged, inFlightRef, completedCacheRef),
    [buildRequestParams, fetchCompanyCertPaged]
  );

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
    </a>
  );

  const [expandedKeys, setExpandedKeys] = useState({});
  const closeMenu = useCallback(() => {
    setTimeout(() => {
      OverflowMenuProvider.setVisible?.(null);
      setExpandedKeys({});
    }, 0);
  }, []);

  const hasApplicableStandardMask = useMemo(
    () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, mergeCertStateListColumnVisibilityFromSaved),
    [state.standard_mask_filters]
  );

  const openMaskDialog = useCallback(
    createOpenMaskDialog({
      columnsVisible,
      setColumnsVisible,
      defaultColumnVisibility: DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeCertStateListColumnVisibilityFromSaved,
      setMaskVisible,
      closeMenu,
    }),
    [columnsVisible, closeMenu]
  );

  const saveUserMaskColumns = useCallback(
    createSaveUserMaskColumns({
      maskPage: MASK_PAGE,
      getTable,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      setMaskVisible,
      toast,
    }),
    [columnsVisible, getTable, setUserMaskFilter, toast]
  );

  const confirmSaveStandardMask = useCallback(
    createConfirmSaveStandardMask({
      maskPage: MASK_PAGE,
      getTable,
      setStandardMaskFilter,
      defaultColumnVisibility: DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY,
      toast,
    }),
    [getTable, setStandardMaskFilter, toast]
  );

  const confirmApplyStandardLayoutToUserMask = useCallback(
    createConfirmApplyStandardLayoutToUserMask({
      maskPage: MASK_PAGE,
      state,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeCertStateListColumnVisibilityFromSaved,
      toast,
      closeMenu,
      onAppliedToTable: ({ colsForSave, attributes }) => {
        const table = getTable();
        if (!table) return;
        isRestoringRef.current = true;
        applyMaintenanceCompanyCertStateListColumnLayout(
          table,
          attributes,
          MAINTENANCE_COMPANY_CERT_STATE_LIST_FIELD_ORDER
        );
        setColumnsVisible(colsForSave);
        applyCertStateListDataColumnVisibility(table, colsForSave);
        setTimeout(() => {
          isRestoringRef.current = false;
          table.redraw?.(true);
          captureHScroll();
          refreshFromStart();
        }, 0);
      },
    }),
    [state, closeMenu, getTable, setUserMaskFilter, toast, captureHScroll, refreshFromStart]
  );

  const setMask = useCallback(
    createMaskSetFieldHandler({
      defaultColumnVisibility: DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyCertStateListDataColumnVisibility,
      toast,
    }),
    [columnsVisible, getTable, toast]
  );

  const maskSelectAll = useCallback(
    createMaskSelectAllHandler({
      defaultColumnVisibility: DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyCertStateListDataColumnVisibility,
    }),
    [getTable]
  );

  const maskDeselectAll = useCallback(
    createMaskDeselectAllHandler({
      defaultColumnVisibility: DEFAULT_CERT_STATE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyCertStateListDataColumnVisibility,
    }),
    [getTable]
  );

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const akcieSubItems = buildMaintenanceMaskAkcieSubItems({
    maskPage: MASK_PAGE,
    getTable,
    setUserMaskFilter,
    cleanSorters,
    captureHScroll,
    refreshFromStart,
    closeMenu,
    openMaskDialog,
    openChosenNotificationPreferences,
    hasApplicableStandardMask,
    confirmApplyStandardLayoutToUserMask,
    itemRenderer,
  });

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: hideRemoteFilterAkcie
        ? akcieSubItems.filter((it) => !['0_1', '0_2'].includes(it.key))
        : akcieSubItems,
    },
  ];

  function previewFilter(data, filterParams) {
    if (filterParams.time_period === 'after') {
      return (
        data._children.length === 0 ||
        (data._children.length > 0 && new Date(data._children[0].end_of_validity) < new Date())
      );
    }
    if (filterParams.time_period === '15') {
      return (
        data._children.length > 0 &&
        new Date(data._children[0].end_of_validity) > new Date() &&
        new Date(data._children[0].end_of_validity) <= new Date(new Date().setDate(new Date().getDate() + 15))
      );
    }
    if (filterParams.time_period === '30') {
      return (
        data._children.length > 0 &&
        new Date(data._children[0].end_of_validity) > new Date() &&
        new Date(data._children[0].end_of_validity) <= new Date(new Date().setDate(new Date().getDate() + 30))
      );
    }
    if (filterParams.time_period === 'others') {
      return (
        data._children.length > 0 &&
        new Date(data._children[0].end_of_validity) > new Date(new Date().setDate(new Date().getDate() + 30))
      );
    }
    return false;
  }

  function periodSegmentFilter(data, filterParams) {
    const children = (data._children || [])
      .slice()
      .sort((a, b) => new Date(a.start_of_validity || 0) - new Date(b.start_of_validity || 0));
    const dateFrom = filterParams.dateFrom instanceof Date ? filterParams.dateFrom : new Date(filterParams.dateFrom);
    const dateTo = filterParams.dateTo instanceof Date ? filterParams.dateTo : new Date(filterParams.dateTo);
    const wantOnTime = filterParams.segment === 'on_time';
    for (let idx = 0; idx < children.length; idx++) {
      const rec = children[idx];
      const start = rec.start_of_validity ? new Date(rec.start_of_validity) : null;
      const end = rec.end_of_validity ? new Date(rec.end_of_validity) : null;
      if (!start || !end || start > dateTo || end < dateFrom) continue;
      const prev = children[idx - 1];
      const prevEnd = prev && prev.end_of_validity ? new Date(prev.end_of_validity) : null;
      const isOnTime = !prevEnd || start <= prevEnd;
      if (isOnTime === wantOnTime) return true;
    }
    return false;
  }

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" title={t('Common.Print')} onClick={() => getTable()?.print?.(false, true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')}
              onClick={
                searchIconColor !== 'active'
                  ? () => setVisibleSearch(true)
                  : () => {
                      setSearchIconColor('');
                      setSearchValue('');
                      getTable()?.clearFilter?.();
                      refreshFromStart();
                    }
              }
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" />
              ) : (
                <MdSearchOff fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" title={t('Common.ExportToExcel')} onClick={() => getTable()?.download?.('xlsx', 'data.xlsx', { sheetName: t('Print.Title.MaintenanceCompanyCertStateList') })}>
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" title={t('Common.Refresh')} onClick={() => { captureHScroll(); refreshFromStart(); }}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
            <button type="button" title={t('Icon.First')} onClick={() => scrollToFirst()}>
              <BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
            <button type="button" title={t('Icon.Previous')} onClick={() => scrollUp()}>
              <BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
            <button type="button" title={t('Icon.Next')} onClick={() => scrollDown()}>
              <ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
            <button type="button" title={t('Icon.Last')} onClick={() => scrollToLast()}>
              <ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button
              type="button"
              title={t('Common.AddToFavorites')}
              className="disabled:opacity-25"
              disabled={favoriteIcon}
              onClick={() => openFavoriteDialog(t('Print.Title.MaintenanceCompanyCertStateList'), MASK_PAGE)}
            >
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" title={t('Common.QuickView')} onClick={() => setFastView(MASK_PAGE, columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <div ref={elementRef}>
                <ReactTabulator
                  key={`mntn010-${preview ?? ''}-${preview_period?.dateFrom ?? ''}-${preview_period?.dateTo ?? ''}-${preview_segment ?? ''}`}
                  onRef={(r) => {
                    ref.current = r?.current;
                  }}
                  className="h-[calc(100vh_-_193px)]"
                  columns={columns}
                  events={{
                    rowSelectionChanged: onSelectRow,
                    tableBuilt: () => {
                      const table = getTable();
                      if (!table) return;
                      isRestoringRef.current = true;
                      if (preview_period && preview_segment) {
                        const dateFrom = new Date(preview_period.dateFrom);
                        const dateTo = new Date(preview_period.dateTo);
                        table.setFilter(periodSegmentFilter, { dateFrom, dateTo, segment: preview_segment });
                      } else if (preview != null) {
                        table.setFilter(previewFilter, { time_period: preview });
                      }
                      if (!isPreviewPeriodMode) {
                        const effective = getEffectiveMask(MASK_PAGE, state);
                        if (effective?.type === 'user' && effective.userMask) {
                          restoreMaintenanceCompanyCertStateListUserMaskToTable(table, effective.userMask, {
                            setColumnsVisible,
                            applyColumnVisibilityFn: (tbl, jc, setVis) => {
                              const merged = mergeCertStateListColumnVisibilityFromSaved(jc);
                              setVis(merged);
                              applyCertStateListDataColumnVisibility(tbl, merged);
                            },
                          });
                        } else if (effective?.type === 'standard' && effective.standardMask) {
                          const m = effective.standardMask;
                          applyMaintenanceCompanyCertStateListColumnLayout(
                            table,
                            safeParse(m.attributes, []),
                            MAINTENANCE_COMPANY_CERT_STATE_LIST_FIELD_ORDER
                          );
                          const jsonColumns = safeParse(m.columns, {});
                          if (jsonColumns && typeof jsonColumns === 'object') {
                            const merged = mergeCertStateListColumnVisibilityFromSaved(jsonColumns);
                            setColumnsVisible(merged);
                            applyCertStateListDataColumnVisibility(table, merged);
                          }
                        }
                      }
                      setTimeout(() => {
                        isRestoringRef.current = false;
                      }, 0);
                      tableBuiltRef.current = true;
                      refreshFromStart();
                    },
                    ...getTabulatorPersistLayoutEvents(MASK_PAGE, ref, setUserMaskFilter),
                    headerFilterChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    sortChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    renderComplete: () => restoreHScroll(),
                  }}
                  options={{
                    renderHorizontal: 'virtual',
                    rowHeight: 42,
                    layout: 'fitColumns',
                    movableColumns: true,
                    headerSortTristate: true,
                    dataTree: true,
                    dataTreeStartExpanded: false,
                    dataTreeFilter: false,
                    dataTreeElementColumn: 'company.name',
                    dataTreeCollapseElement: ReactDOMServer.renderToStaticMarkup(<CollapseTreeIcon />),
                    dataTreeExpandElement: ReactDOMServer.renderToStaticMarkup(<ExpandTreeIcon />),
                    dataTreeBranchElement: ReactDOMServer.renderToStaticMarkup(<BranchTreeIcon />),
                    printAsHtml:true,printHeader: () => buildCanonicalPrintHeaderHtml({
                      branding: state?.branding,
                      productFallbackSrc: defaultProductLogoSrc,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                      title: t('Print.Title.MaintenanceCompanyCertStateList'),
                      printDateLabel: t('Print.DateLabel'),
                    }),
                    printFooter: () => buildCanonicalPrintFooterHtml({
                      companySettingsPrint: state?.company_settings_print,
                    }),
                    printRowRange: 'active',
                    ajaxURL: '/api/company_certificates/paged',
                    progressiveLoad: 'scroll',
                    progressiveLoadScrollMargin: 80,
                    paginationSize: pageSizePc,
                    pagination: false,
                    filterMode: 'remote',
                    sortMode: 'remote',
                    ajaxRequestFunc,
                    dataLoader: true,
                    dataLoaderLoading: t('Common.LoadingData'),
                    dataLoaderError: t('Common.LoadFailed'),
                    ajaxRequesting: () => {
                      if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                      const table = getTable();
                      if (table?.initialized) table.alert?.(t('Common.LoadingData'));
                      return true;
                    },
                    ajaxResponse: (url, params, response) => {
                      getTable()?.clearAlert?.();
                      return response;
                    },
                    ajaxError: () => {
                      getTable()?.clearAlert?.();
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
          />
          <button type="button" onClick={() => searchTable()} className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600">
            <SearchIcon />
          </button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
          </div>
          {columns.map((item) => {
            if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
            return (
              <div key={item.field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <MaintenanceMaskDialogCheckbox field={item.field} inputId={`mcssl-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} />
                <label htmlFor={`mcssl-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}
                </label>
              </div>
            );
          })}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
        </div>
      </Dialog>
    </>
  );
}
